import React, { useCallback, useMemo, useState } from 'react';

import { CheckCircleOutlined, Visibility, VisibilityOff } from '@material-ui/icons';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Divider,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import {
    CardContentStyled,
    CardStyled,
    CollapsibleCard
} from '../../../../../components/common/collapsible/CollapsibleCard';
import { IntegrationTypes } from '../../../../../types/integrationTypes';
import { LTIVersion } from '../../../AdminIntegrationsContainer';
import AdminSettingsContainer from '../../../AdminSettingsContainer';

const AdvancedOptionsDiv = styled.div`
    margin-top: 24px;
`;

const AttributeNameInput = styled(TextField)`
    width: 400px;
`;

const FieldLabelDiv = styled.div`
    margin-top: 12px;
`;

const StyledSelect = styled(Select)`
    width: 400px;
`;

const SmallSelect = styled(Select)`
    width: 80px;
`;

const SmallTD = styled.td`
    width: 80px;
`;

const SmallTable = styled.table`
    width: 200px;
`;

const SelectDiv = styled.div`
    display: flex;
    flex-direction: column;
`;

const ExtendedDivider = styled(Divider)`
    margin-top: 12px;
    margin-bottom: 12px;
`;

enum SyncType {
    AllClasses = 'all-classes',
    SomeClasses = 'some-classes'
}

export enum AuthType {
    UserOauthToken = 'user-oauth-token',
    ClientCredentials = 'client-credentials',
    ApiToken = 'api-token',
    JwtBearer = 'jwt-bearer'
}

enum ContentLearningTools {
    LmsContent = 'lms-content',
    InstructorChoice = 'instructor-choice'
}

const currentUserTimezone = new Date().getTimezoneOffset() * 60 * 1000;

const LmsOptions = ({
    integrationType,
    lmsBaseUrl,
    lmsKey,
    lmsSecret,
    syncSettings,
    handleChange,
    adminUsername,
    configData,
    ltiVersion
}) => {
    const { t } = useTranslation('admin_settings');
    const { adminSettings } = useContainer(AdminSettingsContainer);
    const { options } = adminSettings.featureSettings;
    const { hasMoodle } = options;

    const {
        enable_roster: enableRoster,
        enable_attendance: enableAttendance,
        enable_sessions: enableSessions,
        enable_content_picker: enableContentPicker,
        enable_attendance_grade: enableAttendanceGrade,
        attendance_grade_tardy_score: attendanceGradeTardyScore,
        enable_content: enableContent,
        sync_type: syncType,
        auth_type: authType = AuthType.UserOauthToken,
        sync_course_ids: syncCourseIds,
        content_learning_tools: contentLmsTools,
        sync_count: syncCount = 0,
        sync_times: syncTimes
    } = syncSettings || {};
    const requiresConnectionConfig = integrationType !== IntegrationTypes.IMS;

    const canSyncContent =
        true || (integrationType !== IntegrationTypes.IMS && integrationType !== IntegrationTypes.CSOD);
    const canSyncSessions = integrationType === IntegrationTypes.Docebo || integrationType === IntegrationTypes.CSOD;
    const canSelectLMSContent = integrationType === IntegrationTypes.CSOD;
    const canSelectAuthType = integrationType == IntegrationTypes.Canvas;
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const clonedSyncTimes = useMemo(() => {
        let syncTimesTemp = _.cloneDeep(syncTimes);

        if (!syncTimesTemp) {
            syncTimesTemp = {};
        }

        for (let syncContentIndex = 0; syncContentIndex <= 5; syncContentIndex++) {
            if (syncContentIndex >= syncCount) {
                // delete removed times if user decreased sync_count
                delete syncTimesTemp[String(syncContentIndex)];
            } else if (!syncTimesTemp[String(syncContentIndex)]) {
                // add default for newly added time
                syncTimesTemp[String(syncContentIndex)] = {
                    hours: 12,
                    minutes: 0,
                    ampm: 'AM',
                    timezone_offset: currentUserTimezone
                };
            } else if (syncTimesTemp[String(syncContentIndex)].timezone_offset !== currentUserTimezone) {
                // adjust timezone offset based on current user timezone
                const {
                    hours,
                    minutes,
                    ampm,
                    timezone_offset: originalOffset
                } = syncTimesTemp[String(syncContentIndex)];

                const originalDate = new Date();
                originalDate.setHours(ampm === 'AM' ? hours : hours + 12);
                originalDate.setMinutes(minutes);

                // calculate the UTC time out of the original time offset
                const utcTime = new Date(originalDate.getTime() + originalOffset);
                // create a new Date representing the original time in the current user timezone
                const userDate = new Date(utcTime.getTime() - currentUserTimezone);

                syncTimesTemp[String(syncContentIndex)] = {
                    hours: userDate.getHours() % 12 || 12,
                    minutes: userDate.getMinutes(),
                    ampm: userDate.getHours() > 12 ? 'PM' : 'AM',
                    timezone_offset: currentUserTimezone
                };
            }
        }
        return syncTimesTemp;
    }, [syncCount, syncTimes]);

    const handleSyncSettingsChange = useCallback(
        (event) => {
            const { name, value, checked } = event.target;
            console.log('name, value, checked', name, value, checked);
            const attendanceOverride =
                name === 'enable_roster' && !checked
                    ? {
                          enable_attendance: false,
                          enable_attendance_grade: false
                      }
                    : {};

            handleChange({
                target: {
                    name: 'sync_settings',
                    value: {
                        ...syncSettings,
                        [name]: [
                            'enable_roster',
                            'enable_attendance',
                            'enable_content',
                            'enable_attendance_grade',
                            'enable_sessions'
                        ].includes(name)
                            ? checked
                            : value,
                        ...attendanceOverride
                    }
                }
            });
        },
        [handleChange, syncSettings]
    );
    const handleExtraAdminConfigPropertiesChange = useCallback(
        (event) => {
            const { name, value } = event.target;
            handleChange({
                target: {
                    name: 'config_data',
                    value: {
                        ...configData,
                        [name]: value
                    }
                }
            });
        },
        [configData, handleChange]
    );

    const handleSyncTimeChange = useCallback(
        (event) => {
            const { name, value } = event.target;
            const [syncTimesIndex, syncTimesAttribute] = name.split('_');
            clonedSyncTimes[syncTimesIndex][syncTimesAttribute] = value;
            clonedSyncTimes[syncTimesIndex].timezone_offset = currentUserTimezone;
            handleSyncSettingsChange({
                target: {
                    name: 'sync_times',
                    value: clonedSyncTimes
                }
            });
        },
        [clonedSyncTimes, handleSyncSettingsChange]
    );
    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const hasConnectionConfigFilled = useMemo(
        () =>
            !Boolean(requiresConnectionConfig) ||
            (Boolean(lmsBaseUrl) && Boolean(lmsSecret) && (authType !== AuthType.UserOauthToken || Boolean(lmsKey))),
        [requiresConnectionConfig, lmsBaseUrl, lmsSecret, authType, lmsKey]
    );
    const hasAnyFeatureEnabled = enableContent || enableAttendance || enableRoster || enableSessions;
    const hasCroneJobFeatureEnabled = enableContent || enableRoster || enableSessions;

    const connectionConfigRows = useMemo(() => {
        const result = [
            {
                key: 'lms_base_url',
                element: (
                    <FieldLabelDiv>
                        <InputLabel>{t('lms-base-url-name')}</InputLabel>
                        <AttributeNameInput
                            size="small"
                            name="lms_base_url"
                            value={lmsBaseUrl}
                            onChange={handleChange}
                        />
                    </FieldLabelDiv>
                )
            }
        ];

        if (canSelectAuthType) {
            result.push({
                key: 'lms_auth_type',
                element: (
                    <SelectDiv>
                        <InputLabel id="lms-select-auth-type" disabled={!canSelectAuthType}>
                            {t('lms-auth-type.select-label')}
                        </InputLabel>
                        <StyledSelect
                            name="auth_type"
                            value={authType}
                            label={t('lms-auth-type.select-label')}
                            labelId="select-auth-type"
                            onChange={handleSyncSettingsChange}
                            size="small"
                            disabled={!canSelectAuthType}
                        >
                            {[AuthType.UserOauthToken, AuthType.ApiToken].map((authType) => (
                                <MenuItem key={`lms-sync-type-${authType}`} value={authType}>
                                    <Tooltip title={t(`lms-auth-type.${authType}-tooltip`)} placement="right">
                                        <Typography>{t(`lms-auth-type.${authType}`)}</Typography>
                                    </Tooltip>
                                </MenuItem>
                            ))}
                        </StyledSelect>
                    </SelectDiv>
                )
            });
        }

        const lmsSecretRow = (label) => ({
            key: 'lms_secret',
            element: (
                <FieldLabelDiv>
                    <InputLabel> {label}</InputLabel>
                    <AttributeNameInput size="small" name="lms_secret" value={lmsSecret} onChange={handleChange} />
                </FieldLabelDiv>
            )
        });

        switch (authType) {
            case AuthType.UserOauthToken:
                result.push(
                    {
                        key: 'lms_key',
                        element: (
                            <FieldLabelDiv>
                                <InputLabel>{t('lms-key-name')}</InputLabel>
                                <AttributeNameInput
                                    size="small"
                                    name="lms_key"
                                    value={lmsKey}
                                    onChange={handleChange}
                                />
                            </FieldLabelDiv>
                        )
                    },
                    lmsSecretRow(t('lms-secret-name'))
                );
                break;
            case AuthType.ApiToken:
                result.push(lmsSecretRow(t('lms-token-name')));
                break;
        }

        switch (integrationType) {
            case IntegrationTypes.Docebo: {
                result.push({
                    key: 'admin_username',
                    element: (
                        <FieldLabelDiv>
                            <InputLabel> {t('lms-admin-username')}</InputLabel>
                            <AttributeNameInput
                                size="small"
                                name="admin_username"
                                value={adminUsername}
                                onChange={handleChange}
                            />
                        </FieldLabelDiv>
                    )
                });
                break;
            }
            case IntegrationTypes.CSOD: {
                result.push({
                    key: 'csod_vilt_auth_username',
                    element: (
                        <FieldLabelDiv>
                            <InputLabel> {t('csod-vilt-auth-username')}</InputLabel>
                            <AttributeNameInput
                                size="small"
                                name="csod_vilt_auth_username"
                                value={configData?.csod_vilt_auth_username}
                                onChange={handleExtraAdminConfigPropertiesChange}
                            />
                        </FieldLabelDiv>
                    )
                });
                result.push({
                    key: 'csod_vilt_auth_password',
                    element: (
                        <FieldLabelDiv>
                            <InputLabel> {t('csod-vilt-auth-password')}</InputLabel>
                            <AttributeNameInput
                                size="small"
                                name="csod_vilt_auth_password"
                                value={configData?.csod_vilt_auth_password}
                                onChange={handleExtraAdminConfigPropertiesChange}
                                type={showPassword ? 'text' : 'password'}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? t('hide-password') : t('display-password')}
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            onMouseUp={handleMouseUpPassword}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FieldLabelDiv>
                    )
                });
                break;
            }
        }

        return result;
    }, [
        adminUsername,
        authType,
        canSelectAuthType,
        configData,
        handleChange,
        handleExtraAdminConfigPropertiesChange,
        handleSyncSettingsChange,
        integrationType,
        lmsBaseUrl,
        lmsKey,
        lmsSecret,
        showPassword,
        t
    ]);

    const cardLmsConnectionConfig = requiresConnectionConfig ? (
        <CollapsibleCard
            title={t('lms-sync-connection-config')}
            titleIcon={hasConnectionConfigFilled ? <CheckCircleOutlined /> : undefined}
            id="lms-sync-connection-config"
            defaultExpanded={!hasConnectionConfigFilled}
            rows={connectionConfigRows}
        />
    ) : null;

    const cardCommonSettings = (
        <CardStyled>
            <CardContentStyled>
                <SelectDiv>
                    <InputLabel id="lms-select-sync-type-label" disabled={!hasAnyFeatureEnabled}>
                        {t('lms-select-sync-type')}
                    </InputLabel>
                    <StyledSelect
                        name="sync_type"
                        value={syncType}
                        label={t('lms-select-sync-type')}
                        labelId="select-sync-type"
                        onChange={handleSyncSettingsChange}
                        size="small"
                        disabled={!hasAnyFeatureEnabled}
                    >
                        {Object.values(SyncType).map((contentSyncType) => (
                            <MenuItem key={`lms-sync-type-${contentSyncType}`} value={contentSyncType}>
                                {t(contentSyncType)}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </SelectDiv>
                {syncType === SyncType.SomeClasses && (
                    <FieldLabelDiv>
                        <InputLabel id="lms-select-sync-course-ids-label" disabled={!hasAnyFeatureEnabled}>
                            {t('lms-sync-course-ids')}
                        </InputLabel>
                        <AttributeNameInput
                            size="small"
                            name="sync_course_ids"
                            value={syncCourseIds}
                            onChange={handleSyncSettingsChange}
                            disabled={!hasAnyFeatureEnabled}
                        />
                    </FieldLabelDiv>
                )}
                <ExtendedDivider />
                <SelectDiv>
                    <InputLabel id="lms-select-sync-count-label" disabled={!hasCroneJobFeatureEnabled}>
                        {t('lms-sync-count')}
                    </InputLabel>
                    <StyledSelect
                        name="sync_count"
                        value={syncCount}
                        label={t('lms-sync-count')}
                        labelId="select-lms-sync-count"
                        onChange={handleSyncSettingsChange}
                        size="small"
                        disabled={!hasCroneJobFeatureEnabled}
                    >
                        {[...Array(5).keys()].map((i) => (
                            <MenuItem key={`lms-sync-count-${i}`} value={i}>
                                {i}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </SelectDiv>
                {_.values(clonedSyncTimes).map((clonedSyncTime, i) => (
                    <SelectDiv key={i}>
                        <InputLabel id={`lms-sync-time-${i}`} disabled={!hasCroneJobFeatureEnabled}>
                            {t(`sync-time-${i}`)}
                        </InputLabel>
                        <SmallTable>
                            <tbody>
                                <tr>
                                    <SmallTD>
                                        <SmallSelect
                                            name={`${i}_hours`}
                                            value={clonedSyncTime.hours}
                                            onChange={handleSyncTimeChange}
                                            size="small"
                                            disabled={!hasCroneJobFeatureEnabled}
                                        >
                                            {[...Array(12).keys()].map((itemIndex) => (
                                                <MenuItem
                                                    key={`lms-sync-time-1-${itemIndex + 1}`}
                                                    value={itemIndex + 1}
                                                >
                                                    {itemIndex + 1}
                                                </MenuItem>
                                            ))}
                                        </SmallSelect>
                                    </SmallTD>
                                    <SmallTD>:</SmallTD>
                                    <SmallTD>
                                        <SmallSelect
                                            name={`${i}_minutes`}
                                            value={clonedSyncTime.minutes}
                                            onChange={handleSyncTimeChange}
                                            size="small"
                                            disabled={!hasCroneJobFeatureEnabled}
                                        >
                                            {['00', '15', '30', '45'].map((minutesStr) => (
                                                <MenuItem
                                                    key={`lms-sync-minutes-0-${minutesStr}`}
                                                    value={Number(minutesStr)}
                                                >
                                                    {minutesStr}
                                                </MenuItem>
                                            ))}
                                        </SmallSelect>
                                    </SmallTD>
                                    <SmallTD>
                                        <SmallSelect
                                            name={`${i}_ampm`}
                                            value={clonedSyncTime.ampm}
                                            onChange={handleSyncTimeChange}
                                            size="small"
                                            disabled={!hasCroneJobFeatureEnabled}
                                        >
                                            <MenuItem key={`lms-sync-minutes-0-${i}`} value={'AM'}>
                                                {'AM'}
                                            </MenuItem>
                                            <MenuItem key={`lms-sync-minutes-15-${i}`} value={'PM'}>
                                                {'PM'}
                                            </MenuItem>
                                        </SmallSelect>
                                    </SmallTD>
                                </tr>
                            </tbody>
                        </SmallTable>
                    </SelectDiv>
                ))}
            </CardContentStyled>
        </CardStyled>
    );

    return (
        <AdvancedOptionsDiv>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography>{t('sync-settings')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {cardLmsConnectionConfig}
                    {cardCommonSettings}

                    <CollapsibleCard
                        title={t('lms-enable-roster-sync')}
                        id="enable-roster"
                        toggle={{
                            name: 'enable_roster',
                            checkedState: enableRoster,
                            onChange: handleSyncSettingsChange
                        }}
                        rows={[]}
                    />

                    {integrationType === 'Blackboard' || integrationType === 'Docebo' ? (
                        <CollapsibleCard
                            title={t('lms-enable-attendance-sync')}
                            id="enable-attendance"
                            toggle={{
                                name: 'enable_attendance',
                                hint: t('lms-enable-attendance-sync-hint'),
                                checkedState: enableAttendance,
                                onChange: handleSyncSettingsChange,
                                disabled: !enableRoster
                            }}
                            rows={[]}
                        />
                    ) : (
                        ltiVersion === LTIVersion.V1_3 && (
                            <CollapsibleCard
                                title={t('lms-enable-attendance-sync')}
                                id="enable-attendance"
                                toggle={{
                                    name: 'enable_attendance_grade',
                                    hint: t('lms-enable-attendance-sync-hint'),
                                    checkedState: enableAttendanceGrade,
                                    onChange: handleSyncSettingsChange,
                                    disabled: !enableRoster
                                }}
                                rows={[
                                    {
                                        key: 'lms-attendance-tardy-score',
                                        element: (
                                            <InputLabel id="lms-attendance-tardy-score-label">
                                                {t('lms-attendance-sync-tardy-score-using-system-feature-settings')}
                                            </InputLabel>
                                        )
                                    }
                                ]}
                            />
                        )
                    )}

                    {canSyncContent && (
                        <CollapsibleCard
                            title={t('lms-enable-content-sync')}
                            id="enable-content"
                            toggle={{
                                name: 'enable_content',
                                checkedState: enableContent,
                                onChange: handleSyncSettingsChange
                            }}
                            rows={
                                !hasMoodle
                                    ? []
                                    : [
                                          {
                                              key: 'lms-content-learning-tools',
                                              element: (
                                                  <SelectDiv>
                                                      <InputLabel id="select-content-learning-tools">
                                                          {t('lms-select-learning-tools')}
                                                      </InputLabel>
                                                      <StyledSelect
                                                          name="content_learning_tools"
                                                          value={contentLmsTools || ContentLearningTools.LmsContent}
                                                          label={t('lms-select-learning-tools')}
                                                          labelId="lms-select-learning-tools"
                                                          onChange={handleSyncSettingsChange}
                                                          size="small"
                                                      >
                                                          {Object.values(ContentLearningTools).map((learningTool) => (
                                                              <MenuItem
                                                                  key={`lms-select-learning-tool-${learningTool}`}
                                                                  value={learningTool}
                                                              >
                                                                  {t(`lms-learning-tool-${learningTool}`)}
                                                              </MenuItem>
                                                          ))}
                                                      </StyledSelect>
                                                  </SelectDiv>
                                              )
                                          }
                                      ]
                            }
                        />
                    )}
                    {canSyncSessions && (
                        <CollapsibleCard
                            title={t('lms-enable-sessions-sync')}
                            id="enable-sessions"
                            toggle={{
                                name: 'enable_sessions',
                                checkedState: enableSessions,
                                onChange: handleSyncSettingsChange
                            }}
                            rows={[]}
                        />
                    )}
                    {canSelectLMSContent && (
                        <CollapsibleCard
                            title={t('lms-enable-content-picker')}
                            id="enable-content-picker"
                            toggle={{
                                name: 'enable_content_picker',
                                checkedState: enableContentPicker,
                                onChange: handleSyncSettingsChange
                            }}
                            rows={[]}
                        />
                    )}
                </AccordionDetails>
            </Accordion>
        </AdvancedOptionsDiv>
    );
};
export default LmsOptions;
